import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { 
  ChevronLeft, 
  ChevronRight, 
  Package as PackageIcon, 
  Building2,
  Loader2 
} from "lucide-react";
import PackageCard from "./PackageCard";
import { usePackageCartActions } from "../../hooks/usePackageCartActions";
import { useCartActions } from "../../hooks/useCartActions";
import ReplaceCartPopup from "./ReplaceCartPopup";

export default function Packages() {
  const navigate = useNavigate();
  const cart = useSelector((state) => state.cart.items || []);
  
  const [packages, setPackages] = useState([]);
  const [popularLabs, setPopularLabs] = useState([]);
  const [selectedLabId, setSelectedLabId] = useState("all");
  const [loading, setLoading] = useState(true);

  const packageSliderRef = useRef(null);
  const { addPackage } = usePackageCartActions();
  const { LoginPopupComponent } = useCartActions();

  // Load packages & labs dynamically (matching LabList.js and LandingPage.js)
  useEffect(() => {
    let isMounted = true;

    const fetchPackagesAndLabs = async () => {
      try {
        setLoading(true);

        const [pkgRes, labsRes] = await Promise.all([
          axios.get("https://admin.medrphalabs.com/api/Package?id=0").catch(() => ({ data: [] })),
          axios.get("https://admin.medrphalabs.com/api/Lab").catch(() => ({ data: [] })),
        ]);

        let pkgList = [];
        if (Array.isArray(pkgRes.data)) {
          pkgList = pkgRes.data;
        } else if (Array.isArray(pkgRes.data?.data)) {
          pkgList = pkgRes.data.data;
        }

        let labsList = [];
        if (Array.isArray(labsRes.data)) {
          labsList = labsRes.data;
        } else if (Array.isArray(labsRes.data?.data)) {
          labsList = labsRes.data.data;
        }

        // Fallback to MasterAPI if empty
        if (pkgList.length === 0) {
          const API_BASE_URL = (process.env.REACT_APP_API_URL || "https://online-tech.in/api").replace(/\/+$/, "");
          const res = await axios.get(`${API_BASE_URL}/MasterAPI/GetTestPackagesAll?pageNumber=1&pageSize=100`).catch(() => ({ data: {} }));
          if (res.data?.succeeded && Array.isArray(res.data?.data)) {
            pkgList = res.data.data;
          }
        }

        if (isMounted) {
          const mappedPackages = pkgList
            .filter((p) => p.isActive !== false && p.isActive !== 0 && (p.packageName || p.name))
            .map((pkg) => {
              let imgUrl = null;
              if (pkg.image && typeof pkg.image === "string" && pkg.image.trim()) {
                const clean = pkg.image.trim();
                if (clean.startsWith("http")) {
                  imgUrl = clean;
                } else if (clean.startsWith("PackageImage/") || clean.startsWith("LabImage/") || clean.startsWith("TestImage/")) {
                  imgUrl = `https://admin.medrphalabs.com/${clean}`;
                } else {
                  imgUrl = `https://admin.medrphalabs.com/PackageImage/${clean}`;
                }
              }

              const labObj = labsList.find((l) => Number(l.id) === Number(pkg.labId));
              const labName =
                labObj?.name ||
                labObj?.labName ||
                labObj?.lab_name ||
                (pkg.labId === 1 ? "DGchem Lab" : pkg.labId === 3 ? "DOCOPD LAB" : "Certified Partner Lab");

              return {
                ...pkg,
                dynamicImageUrl: imgUrl,
                assignedLabName: labName,
              };
            });

          setPackages(mappedPackages);

          // Get unique labs from packages
          const uniqueLabIds = [...new Set(mappedPackages.map((p) => p.labId).filter(Boolean))];
          const labsForPackages = uniqueLabIds.map((lId) => {
            const lObj = labsList.find((l) => Number(l.id) === Number(lId));
            return {
              id: lId,
              name: lObj?.name || lObj?.labName || (lId === 1 ? "DGchem Lab" : lId === 3 ? "DOCOPD LAB" : `Lab #${lId}`),
            };
          });
          setPopularLabs(labsForPackages);
        }
      } catch (err) {
        console.error("Error loading packages for Home Page:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchPackagesAndLabs();
    return () => {
      isMounted = false;
    };
  }, []);

  const slidePackages = (direction) => {
    if (packageSliderRef.current) {
      const scrollAmount = 320;
      packageSliderRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const isPackageInCart = (pkgId) => {
    return cart.some(
      (item) => Number(item.productId) === Number(pkgId) && Number(item.categoryId) === 3
    );
  };

  const dynamicPackages =
    selectedLabId === "all"
      ? packages
      : packages.filter((p) => Number(p.labId) === Number(selectedLabId));

  if (!loading && packages.length === 0) {
    return null;
  }

  return (
    <section className="w-full my-6">
      <div className="bg-white border border-slate-200/90 rounded-3xl p-4 sm:p-6 md:p-8 shadow-xs relative">
        
        {/* Header with Title and Sliding Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-2 h-7 bg-gradient-to-b from-red-600 to-rose-600 rounded-full" />
            <div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900 leading-tight">
                Popular Health Checkup Packages
              </h3>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                Showing {dynamicPackages.length} NABL-accredited health packages with free home sample pickup
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end sm:self-auto">
            {/* Slider Arrow Buttons */}
            <button
              onClick={() => slidePackages("left")}
              className="w-9 h-9 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs cursor-pointer"
              title="Slide Left"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => slidePackages("right")}
              className="w-9 h-9 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs cursor-pointer"
              title="Slide Right"
            >
              <ChevronRight size={18} />
            </button>

            <button
              onClick={() => navigate("/all-packages")}
              className="text-xs font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3.5 py-2 rounded-xl transition-all flex items-center gap-1 ml-1 cursor-pointer"
            >
              <span>View All</span>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {/* Dynamic Lab Filter Chips */}
        {popularLabs.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-3 mb-3">
            <button
              onClick={() => setSelectedLabId("all")}
              className={`text-xs font-bold px-3.5 py-1.5 rounded-xl transition-all whitespace-nowrap border flex items-center gap-1.5 cursor-pointer ${
                selectedLabId === "all"
                  ? "bg-red-600 text-white border-red-600 shadow-xs"
                  : "bg-slate-50 text-slate-700 border-slate-200 hover:border-red-300"
              }`}
            >
              <span>All Popular Labs ({packages.length})</span>
            </button>

            {popularLabs.map((lab) => {
              const labPkgCount = packages.filter((p) => Number(p.labId) === Number(lab.id)).length;
              return (
                <button
                  key={lab.id}
                  onClick={() => setSelectedLabId(lab.id)}
                  className={`text-xs font-bold px-3.5 py-1.5 rounded-xl transition-all whitespace-nowrap border flex items-center gap-1.5 cursor-pointer ${
                    Number(selectedLabId) === Number(lab.id)
                      ? "bg-red-600 text-white border-red-600 shadow-xs"
                      : "bg-slate-50 text-slate-700 border-slate-200 hover:border-red-300"
                  }`}
                >
                  <Building2
                    size={12}
                    className={Number(selectedLabId) === Number(lab.id) ? "text-white" : "text-slate-400"}
                  />
                  <span>
                    {lab.name} ({labPkgCount})
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Dynamic Package Cards SLIDER */}
        {loading ? (
          <div className="flex items-center gap-4 overflow-hidden py-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="w-[260px] sm:w-[280px] shrink-0 bg-slate-50 rounded-2xl p-4 border border-slate-200 animate-pulse space-y-3"
              >
                <div className="h-40 bg-slate-200 rounded-xl w-full" />
                <div className="h-4 bg-slate-200 rounded w-3/4" />
                <div className="h-3 bg-slate-100 rounded w-1/2" />
                <div className="h-9 bg-slate-200 rounded-xl w-full" />
              </div>
            ))}
          </div>
        ) : dynamicPackages.length === 0 ? (
          <div className="bg-slate-50 rounded-2xl p-8 text-center border border-slate-200 my-2">
            <PackageIcon size={36} className="text-slate-300 mx-auto mb-2" />
            <p className="text-xs font-bold text-slate-700">No health packages found for selected lab.</p>
            <button
              onClick={() => setSelectedLabId("all")}
              className="mt-3 text-xs font-bold text-red-600 bg-white border border-red-200 px-3.5 py-1.5 rounded-lg cursor-pointer hover:bg-red-50"
            >
              View All Labs Packages
            </button>
          </div>
        ) : (
          <div
            ref={packageSliderRef}
            className="flex items-stretch gap-4 overflow-x-auto no-scrollbar pb-3 pt-1 scroll-smooth"
          >
            {dynamicPackages.map((pkg, idx) => (
              <PackageCard
                key={pkg.packageId || pkg.id || idx}
                pkg={pkg}
                inCart={isPackageInCart(pkg.packageId || pkg.id)}
                onCartToggle={(p) => addPackage(p)}
                className="w-[260px] sm:w-[280px] flex-shrink-0"
              />
            ))}
          </div>
        )}
      </div>

      <ReplaceCartPopup />
      {LoginPopupComponent}
    </section>
  );
}